import { Fragment, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useApi } from 'model/Api';
import { Report } from 'model/Report';
import { Ledger } from 'model/Ledger';
import { RecordListParameter, useDataContext } from 'context/DataStoreContext';
import { WorkProcess } from 'model/WorkProcess';
import { ResourceParam } from 'model/Resource';
import { generalStyle, mediaQurey } from 'helper/Style';
import { convertDate } from 'helper/Converter';
import Layout from 'component/part/Layout';
import List from 'component/part/List';
import BackLink from 'component/part/BackLink';
import H2 from 'component/part/H2';
import Select from 'component/part/Select';

const resourceUrl = process.env.REACT_APP_RESOURCE_ENDPOINT || '';

type ListReport = Report & {
  ledgerName: string;
  leaderName: string;
  workProcessName: string;
  totalCost: number;
};

const reportIndexes = [
  { label: '現場名略称', key: 'shortName' },
  { label: '作業日', key: 'startDatetime' },
  { label: '作業内容', key: 'workProcessId' },
  { label: '現場代理人', key: 'leaderId' },
  { label: '経費' },
  { label: '写真' },
];

const reportDefaultConditions = { with: ['ledger', 'leader', 'resource', 'cost'] };

const style = {
  list: {
    [mediaQurey('min', 'pc')]: {
      ...generalStyle.list(6, { right: [5] }),
    },
    [mediaQurey('max', 'tablet')]: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '1rem',
    },
    ['& > div:nth-of-type(1)']: {
      ...generalStyle.onlyPc,
    },
  },
  listRow: {
    [mediaQurey('min', 'pc')]: {
      ...generalStyle.listRow,
    },
    [mediaQurey('max', 'tablet')]: {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gridTemplateRows: 'repeat(4, auto)',
      gap: '0.5rem',
      padding: '1rem',
      border: 'solid 0.1rem #ccc',
      borderRadius: '1rem',
      backgroundColor: '#eeeeee',
      '& > div:nth-of-type(1)': {
        fontWeight: '700',
      },
      '& > div:nth-of-type(4)': {
        gridRow: '3',
      },
      '& > div:nth-of-type(n+2):nth-of-type(n-4)': {
        fontSize: '1.2rem',
      },
      '& > div:nth-of-type(5)': {
        gridColumn: '2',
        gridRow: '1 / 5',
      },
    },
  },
  reportLink: {
    ...generalStyle.link,
    ['& span']: {
      display: 'inline-block',
    },
  },
  thumbnail: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '12rem',
    height: '12rem',
    '& img': {
      maxWidth: '100%',
      maxHeight: '100%',
    },
  },
};

type ReportResourceThumbnailProps = {
  clientId: number;
  resourceCode: string;
  resource: ResourceParam | undefined;
};

const ReportResourceThumbnail = (props: ReportResourceThumbnailProps) =>
  <div css={style.thumbnail}>
    {props.resource?.thumbnailName ? (
      <img src={`${resourceUrl}/C${props.clientId}/${props.resourceCode}/${props.resource.thumbnailName}`} />
    ) : (
      <Fragment>{props.resource?.originalName || ''}</Fragment>
    )}
  </div>;

const ReportList: React.FC = () => {
  const { state } = useDataContext();
  const { auth: authState, report: reportState } = state || {};
  const user = authState?.user;
  const originalReports = reportState?.list || [];
  const api = useApi();
  const [reports, setReports] = useState<ListReport[]>([]);
  const [ledgers, setLedgers] = useState<Ledger[]>([]);
  const [ledger, setLedger] = useState<Ledger>();
  const [reportConditions, setReportsConditions] = useState<RecordListParameter<'ledger'>['conditions']>({
    ...reportDefaultConditions,
    ...reportState?.conditions,
  });
  const [workProcesses, setWorkProcesses] = useState<WorkProcess[]>([]);

  useEffect(() => {
    (async () => {
      const ledgersResponse = await api.ledger.getsByOwn();
      const workProcessesResponse = await api.workProcess.getsByOwn();
      setLedgers(ledgersResponse);
      setWorkProcesses(workProcessesResponse);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!ledger && reportConditions?.ledgerId) {
        delete reportConditions.ledgerId;
        setReportsConditions({ ...reportConditions });
      } else if (ledger) {
        setReportsConditions({ ...reportConditions, ledgerId: ledger.id });
      }
    })();
  }, [ledger]);

  useEffect(() => {
    (async () => {
      const listReports: ListReport[] = originalReports.flatMap((report) => {
        const workProcess = workProcesses.find(({ id }) => id === report.workProcessId);
        const totalCost = (report.costs || []).reduce((value, { cost }) => value + cost, 0);

        return workProcess ? {
          ...report,
          ledgerName: report.ledger?.shortName || '',
          leaderName: ((user) => user ? `${user.lastName} ${user.firstName}` : '')(report.leader),
          workProcessName: workProcess.name,
          totalCost,
        } : [];
      });
      setReports(listReports);
    })();
  }, [originalReports, workProcesses, ledger]);

  return <Layout>
    <Fragment>
      {!user?.role?.permissions.workSupervise && (
        <BackLink link='/' label='トップ'></BackLink>
      )}
    </Fragment>
    <H2>
      日報一覧
      <Select<Ledger>
        list={ledgers}
        value={ledger}
        blankLabel='全台帳'
        labelKey='shortName'
        onChange={setLedger}
      />
    </H2>
    <List
      type='report'
      indexes={reportIndexes}
      conditions={reportConditions}
      displayPager={true}
      css={style.list}
    >
      {reports.map((report, index) => (
        <div css={style.listRow} key={index}>
          <div>
            <NavLink to={`/report/${report.id}`} css={style.reportLink}>
              <span>{report.ledgerName}</span>
              {report.fixed || (<span>（下書き中）</span>)}
            </NavLink>
          </div>
          <div>{convertDate(report.startDatetime)}</div>
          <div>{report.workProcessName}</div>
          <div>
            <span css={generalStyle.nonPc}>現場代理人：</span>
            {report.leaderName}
          </div>
          <div>&yen;{report.totalCost.toLocaleString()}</div>
          <div>
            <ReportResourceThumbnail
              clientId={1}
              resourceCode={report.resourceCode}
              resource={report.resources?.[0]}
            />
          </div>
        </div>
      ))}
    </List>
  </Layout>;
};

export default ReportList;
